import { useAppDispatch, useTranslate } from '@src/core/hooks';
import { setStepCheckout } from '@src/stores/checkout';
import type { Order } from '@src/domains/orders';
import type { Sale } from '@src/domains/sales';
import type { PaymentOption } from '@src/domains/payments';
import type { DirectLink } from '@src/domains/direct-links';
import { usePaymentOptions } from '@src/features/payments/options';
import {
  isTransactableCreated,
  isTransactablePayableOrCompleted,
  isTransactablePaymentComplete,
  resolveTransactableEntity,
} from '@src/domains/transactables';
import React, { useEffect } from 'react';
import { useWidgetId } from '@src/core/IdProvider';
import { track } from '@src/services/analytics';
import { ExpiredSales, PaymentMethods } from './styles';
import PaymentMethod from './PaymentMethod';
import PaymentSuccess from './PaymentSuccess';
import { ProviderFinancialError } from '@src/features/payments';
import Loading from '@src/components/ui2/Loading';

interface PaymentFormProps {
  order?: Order;
  sale?: Sale;
  directLink?: DirectLink;
  onSuccess?: () => void;
}

const PaymentStep = (props: PaymentFormProps) => {
  const { order, sale, directLink, onSuccess = () => {} } = props;

  const dispatch = useAppDispatch();
  const { i18nElement } = useTranslate();
  const widgetId = useWidgetId();

  const transactable = {
    order,
    sale,
    directLink,
  };

  const {
    options: validMethods,
    loading: isLoadingPaymentOptions,
    selected: selectedOption,
    select: setSelectedOption,
  } = usePaymentOptions(transactable);

  const isSelected = (opt: PaymentOption) =>
    selectedOption != null && opt.paymentMethod.id === selectedOption.paymentMethod.id;

  const isExpired = Boolean(resolveTransactableEntity(transactable)) && !isTransactablePayableOrCompleted(transactable);
  const isCreated = isTransactableCreated(transactable);
  const isPaymentSuccess = isTransactablePaymentComplete(transactable);

  useEffect(() => {
    track('checkoutPaymentFormAccess');

    dispatch(setStepCheckout({ id: widgetId || '', value: 'payment' }));
  }, []);

  useEffect(() => {
    if (isPaymentSuccess) {
      onSuccess();
    }
  }, [isPaymentSuccess]);

  return (
    <>
      {isExpired ? (
        <ExpiredSales>
          <p>
            <strong>{i18nElement('checkout_expiredSale')}</strong>
          </p>

          <p>{i18nElement('checkout_makeYourPurchaseAgain')}</p>
        </ExpiredSales>
      ) : null}

      {!isExpired && isCreated ? (
        validMethods.length > 0 ? (
          <PaymentMethods>
            {validMethods.map((opt) => (
              <PaymentMethod
                key={opt.paymentMethod.id}
                order={order!}
                sale={sale!}
                directLink={directLink!}
                paymentOption={opt}
                selected={isSelected(opt)}
                onClick={() => setSelectedOption(opt)}
              />
            ))}
          </PaymentMethods>
        ) : isLoadingPaymentOptions ? (
          <Loading size={24} fullWidth={false} />
        ) : !isPaymentSuccess ? (
          <ProviderFinancialError horizontalStyle={false} />
        ) : null
      ) : null}

      {!isExpired && isPaymentSuccess ? <PaymentSuccess sale={sale} order={order} /> : null}
    </>
  );
};

export default PaymentStep;
